import React from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { FileText, Mail, Upload, FileStack, Copy, Users, ShieldCheck, Cloud, GitMerge, BarChart3, Repeat, Lock } from "lucide-react";

const ToolCard = ({ icon: Icon, title, desc, to, testid }) => {
  const nav = useNavigate();
  return (
    <button data-testid={testid} onClick={() => nav(to)} className="text-left bg-white border border-border rounded-sm p-4 hover:bg-ice hover:border-atlantic/40 transition-colors nav-link">
      <div className="flex items-center gap-2 mb-1"><Icon className="h-5 w-5 text-atlantic" /><span className="font-serif text-lg text-navy">{title}</span></div>
      <p className="text-sm text-mutedsea">{desc}</p>
    </button>
  );
};

export default function AgencyTools() {
  const { can } = useAuth();
  if (!can.admin) return (
    <div className="p-6"><PageHeader title="Agency Tools" />
      <div className="bg-white border border-border rounded-sm p-10 text-center" data-testid="agency-tools-denied">
        <Lock className="h-8 w-8 mx-auto text-mutedsea mb-2" /><p className="text-slate font-medium">Management access required</p>
      </div>
    </div>
  );
  return (
    <div className="p-6 animate-fade-in" data-testid="agency-tools-page">
      <PageHeader title="Agency Tools" subtitle="Centralized tools, utilities, communication history and the Reassignment Center." />
      <Tabs defaultValue="tools">
        <TabsList className="bg-white border border-border">
          <TabsTrigger value="tools" data-testid="at-tab-tools" className="data-[state=active]:bg-navy data-[state=active]:text-white">Tools</TabsTrigger>
          <TabsTrigger value="utilities" data-testid="at-tab-utilities" className="data-[state=active]:bg-navy data-[state=active]:text-white">Utilities</TabsTrigger>
          <TabsTrigger value="comm" data-testid="at-tab-comm" className="data-[state=active]:bg-navy data-[state=active]:text-white">Communication</TabsTrigger>
          <TabsTrigger value="reassign" data-testid="at-tab-reassign" className="data-[state=active]:bg-navy data-[state=active]:text-white">Reassignment</TabsTrigger>
        </TabsList>

        <TabsContent value="tools" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ToolCard icon={FileText} title="Forms Library" desc="Coverage forms, letters and templates." to="/forms" testid="at-forms" />
            <ToolCard icon={Mail} title="Email & Templates" desc="Email center and message templates." to="/email" testid="at-email" />
            <ToolCard icon={FileStack} title="Documents" desc="Filing cabinet and document management." to="/documents" testid="at-documents" />
            <ToolCard icon={FileText} title="Certificates" desc="Certificate issuance and tracking." to="/certificates" testid="at-certificates" />
          </div>
        </TabsContent>

        <TabsContent value="utilities" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ToolCard icon={Upload} title="CSV / XLSX Import" desc="Bulk data import with rollback." to="/imports" testid="at-import" />
            <ToolCard icon={Upload} title="Policy Document Import" desc="Extract, match, validate and commit policy docs." to="/imports" testid="at-doc-import" />
            <ToolCard icon={Copy} title="Duplicate Review" desc="Conflict center for potential duplicates." to="/conflicts" testid="at-duplicates" />
            <ToolCard icon={Users} title="Unassigned Records" desc="Assign records with no producer/AM/CSR." to="/administration" testid="at-unassigned" />
            <ToolCard icon={GitMerge} title="Data Quality" desc="Data quality reporting and cleanup." to="/reporting" testid="at-data-quality" />
            <ToolCard icon={Cloud} title="Integration Health" desc="Dropbox, Proton, Apollo, LLM, feeds." to="/administration" testid="at-integrations" />
            <ToolCard icon={BarChart3} title="Apollo Usage" desc="Prospect-data usage and limits." to="/administration" testid="at-apollo-usage" />
            <ToolCard icon={ShieldCheck} title="Backup Status" desc="Chronology integrity and backups." to="/administration" testid="at-backups" />
            <ToolCard icon={Users} title="User Administration" desc="Users, roles, invitations and scope." to="/administration" testid="at-users" />
          </div>
        </TabsContent>

        <TabsContent value="comm" className="mt-4">
          <ToolCard icon={Mail} title="Communication History" desc="Search and filter agency communications by client, prospect, policy, claim, producer and date in the Email Center and record chronologies." to="/email" testid="at-comm-history" />
          <p className="text-sm text-mutedsea mt-3">Communications are recorded on each record's chronology and in the Email Center. Open any client, policy or claim to view its full communication timeline.</p>
        </TabsContent>

        <TabsContent value="reassign" className="mt-4">
          <div className="bg-white border border-border rounded-sm p-5" data-testid="at-reassign-panel">
            <div className="flex items-center gap-2 mb-2"><Repeat className="h-5 w-5 text-atlantic" /><h3 className="font-serif text-lg text-navy">Reassignment Center</h3></div>
            <p className="text-sm text-mutedsea mb-3">Bulk reassign a producer / account manager / CSR book — including activities, prospects, opportunities and BOI relationships — with an impact preview, a required reason, and a controlled, audited execution. Historical attribution, commission ledger and chronology are always preserved.</p>
            <ToolCard icon={Repeat} title="Open Reassignment (Book Transfer)" desc="Select from/to users, preview impact, provide a reason, and confirm. Runs in Administration → Users & Roles → Reassign Book." to="/administration" testid="at-open-reassign" />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
